import TabButtons from '@/components/atoms/TabButtons';
import CustomTable from '@/components/molecules/CustomTable';
import SidebarItemViewTemplate from '@/components/templates/SidebarItemViewTemplate';
import { MIN_CHARS_TO_DO_SEARCH } from '@/constants/numberVars';
import useAdminContentList from '@/hooks/content/useAdminContentList';
import ContentModerationFilters from '@/modules/content/moderation/ContentModerationFilters';
import BrandedContentTypePicker from '@/modules/content/branded/components/BrandedContentTypePicker';
import {
  ContentStatusBadge,
  ContentTypeBadge,
  formatContentDate,
} from '@/modules/content/common/contentDisplay';
import { TSortingDirection } from '@/types';
import {
  CONTENT_TYPES,
  TContentListItem,
  TContentStatusFilter,
  TContentType,
} from '@/types/Content.types';
import { HStack, Image } from '@chakra-ui/react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import React, { useMemo } from 'react';

const COLUMNS = ['Thumbnail', 'Title', 'Type', 'Status', 'Published'];

const SORT_FIELD_BY_COLUMN: Record<string, string> = {
  Title: 'title',
  Type: 'type',
  Status: 'status',
  Published: 'publishedAt',
};
const SORT_COLUMN_BY_FIELD: Record<string, string> = {
  title: 'Title',
  type: 'Type',
  status: 'Status',
  publishedAt: 'Published',
};
const SORTABLE_COLUMNS = ['Title', 'Type', 'Status', 'Published'] as const;
const DEFAULT_SORT_FIELD = 'updatedAt';
const DEFAULT_SORT_DIRECTION: TSortingDirection = 'desc';
const SORT_PARAM_REGEX = /^(title|type|status|publishedAt):(asc|desc)$/;

type BrandedContentSearch = {
  status?: TBrandedContentStatusFilter;
  q?: string;
  type?: TContentType;
  sort?: string;
};

// Both soft-deleted states are reviewed from Content Moderation, so they get no
// tab here (items in either state still surface under "All", read-only).
type TBrandedContentStatusFilter = Exclude<TContentStatusFilter, 'removed' | 'withdrawn'>;

const BRANDED_CONTENT_STATUS_FILTERS: TBrandedContentStatusFilter[] = ['all', 'published', 'draft'];
const DEFAULT_STATUS: TBrandedContentStatusFilter = 'all';

const BrandedContent = () => {
  const { status: statusParam, q, type, sort } = Route.useSearch();
  const navigate = Route.useNavigate();
  const topNavigate = useNavigate();

  const [isPickerOpen, setPickerOpen] = React.useState(false);

  const status = statusParam ?? DEFAULT_STATUS;
  const filterText = q ?? '';

  const [sortField, sortDirection] = (
    sort && SORT_PARAM_REGEX.test(sort) ? sort : `${DEFAULT_SORT_FIELD}:${DEFAULT_SORT_DIRECTION}`
  ).split(':') as [string, TSortingDirection];
  const sortingColumn = SORT_COLUMN_BY_FIELD[sortField];

  const setStatus = (next: TBrandedContentStatusFilter) =>
    navigate({ search: (prev) => ({ ...prev, status: next }) });
  const setSearchText = (value: string) =>
    navigate({ search: (prev) => ({ ...prev, q: value ? value : undefined }) });
  const setType = (value?: TContentType) =>
    navigate({ search: (prev) => ({ ...prev, type: value }) });

  const updateSortingColumn = (column: string) => {
    const field = SORT_FIELD_BY_COLUMN[column];
    if (!field) return;
    const nextDirection: TSortingDirection =
      field === sortField && sortDirection === 'desc' ? 'asc' : 'desc';
    navigate({ search: (prev) => ({ ...prev, sort: `${field}:${nextDirection}` }) });
  };

  const {
    isLoading,
    isRefetching,
    pagination,
    data: content,
  } = useAdminContentList({
    filterText,
    status,
    type,
    isPlatformContent: true,
    sortBy: sortField,
    sortDir: sortDirection,
  });

  const isSearching = useMemo(
    () => filterText.length >= MIN_CHARS_TO_DO_SEARCH && isRefetching,
    [filterText, isRefetching],
  );

  const dataWithId = useMemo(() => content ?? [], [content]);

  const tableListItems = useMemo(
    () =>
      dataWithId.map((item) => ({
        id: item.id,
        cells: [
          item.effectiveThumbnailUrl ? (
            <Image
              key={item.id}
              src={item.effectiveThumbnailUrl}
              alt={item.title}
              width="64px"
              height="40px"
              objectFit="cover"
              borderRadius="4px"
            />
          ) : (
            '—'
          ),
          item.title || '/',
          <ContentTypeBadge key={item.id} type={item.type} />,
          <ContentStatusBadge key={item.id} status={item.status} />,
          formatContentDate(item.publishedAt),
        ],
      })),
    [dataWithId],
  );

  const handleSelectType = (selectedType: TContentType) => {
    setPickerOpen(false);
    topNavigate({ to: '/content/branded-content/create/$type', params: { type: selectedType } });
  };

  return (
    <SidebarItemViewTemplate
      title="Branded Content"
      buttonLabel="+ Create MyOmnia content"
      onButtonClick={() => setPickerOpen(true)}
    >
      <HStack width={'100%'} marginTop={'20px'} justifyContent={'space-between'}>
        <TabButtons
          options={[
            { onClick: () => setStatus('all'), labelValue: 'all', labelDisplay: 'All' },
            { onClick: () => setStatus('published'), labelValue: 'published', labelDisplay: 'Published' },
            { onClick: () => setStatus('draft'), labelValue: 'draft', labelDisplay: 'Drafts' },
          ]}
          selectedOptionValue={status}
        />
        <ContentModerationFilters
          searchText={filterText}
          type={type}
          showVisibility={false}
          isSearching={isSearching}
          onSearchChange={setSearchText}
          onTypeChange={setType}
        />
      </HStack>
      <CustomTable<TContentListItem[], string>
        noItemsMessage="No MyOmnia content yet"
        columns={COLUMNS}
        isLoading={isLoading}
        isRefetching={isRefetching}
        isSearching={isSearching}
        tableListItems={tableListItems}
        pagination={pagination}
        dataWithId={dataWithId}
        customColumnGridPercentages={[14, 40, 14, 14, 18]}
        columnsToSortBy={SORTABLE_COLUMNS}
        sortingColumn={sortingColumn}
        sortingDirection={sortDirection}
        updateSortingColumn={updateSortingColumn}
        fullWidth
        hasRowSelect
      />

      <BrandedContentTypePicker
        isOpen={isPickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={handleSelectType}
      />
    </SidebarItemViewTemplate>
  );
};

export const Route = createFileRoute('/_authenticated/_admin/content/branded-content/')({
  component: BrandedContent,
  validateSearch: (search: Record<string, unknown>): BrandedContentSearch => {
    const result: BrandedContentSearch = {};

    if (BRANDED_CONTENT_STATUS_FILTERS.includes(search.status as TBrandedContentStatusFilter)) {
      result.status = search.status as TBrandedContentStatusFilter;
    }
    if (CONTENT_TYPES.includes(search.type as TContentType)) {
      result.type = search.type as TContentType;
    }
    const rawQ = typeof search.q === 'string' ? search.q.trim() : '';
    if (rawQ) {
      result.q = rawQ;
    }
    if (typeof search.sort === 'string' && SORT_PARAM_REGEX.test(search.sort)) {
      result.sort = search.sort;
    }

    return result;
  },
});
